import { DateTime } from 'luxon';
import * as React from 'react';
import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { CancellationPolicy } from '@/api';
import type { Car } from '@/api/cars/types';
import { formatNumber } from '@/core';
import { Card, Text } from '@/ui';
import { Chip, ChipType } from '@/ui/chip';
import { Icon } from '@/ui/icon';
import { Clock } from '@/ui/icons/clock';
import { ShieldCheckmark } from '@/ui/icons/shield-checkmark';

const formatCancelUntil = (car: Car, cancelUntil: string) => {
  const dt = DateTime.fromISO(cancelUntil, { zone: 'utc' }).setZone(
    car.defaultLocation.timezone
  );
  if (!dt.isValid) {
    return cancelUntil;
  }
  return dt.toFormat('EEE, MMM d · h:mm a ZZZZ');
};

interface CancellationPolicyCardItemProps {
  cancellationPolicy: CancellationPolicy;
  car: Car;
  style?: any;
  disabled?: boolean;
  active?: boolean;
  onPress?: () => void;
}

export const CancellationPolicyCardItem = ({
  cancellationPolicy,
  car,
  style,
  disabled = false,
  active = false,
  onPress,
}: CancellationPolicyCardItemProps) => {
  const { styles, theme } = useStyles(stylesheet);
  const [hover, setHover] = React.useState(false);

  const getStyle = () => {
    const showDisabled = disabled;
    let stateStyle = styles.default;

    if (showDisabled) {
      stateStyle = styles.disabled;
    }
    if (hover) {
      stateStyle = styles.hover;
    }
    if (active) {
      stateStyle = styles.active;
    }

    return [stateStyle];
  };

  return (
    <Pressable
      style={[styles.container, style]}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      hitSlop={8}
      onPress={() => onPress?.()}
    >
      <Card style={[styles.card, getStyle()]}>
        <View style={styles.contentContainer}>
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <View
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
            >
              <View style={{ padding: 2 }}>
                {cancellationPolicy.title === 'Fully Refundable' ? (
                  <Icon
                    type="dressed"
                    icon={<ShieldCheckmark />}
                    fillColor={
                      active
                        ? theme.colors.chip.brand.color
                        : theme.colors.icon.color
                    }
                    width={25}
                    height={25}
                  />
                ) : (
                  <Icon
                    type="dressed"
                    icon={<Clock />}
                    fillColor={
                      active
                        ? theme.colors.chip.brand.color
                        : theme.colors.icon.color
                    }
                    width={25}
                    height={25}
                  />
                )}
              </View>

              <Text type="bodyBold">{cancellationPolicy.title}</Text>
            </View>
          </View>

          <View
            style={{
              paddingRight: 16,
              paddingLeft: 8,
              paddingTop: 8,
              gap: 8,
            }}
          >
            <Text
              style={{
                color: theme.colors.text.secondary,
                flex: 1,
              }}
              type="bodyRegular"
            >
              {cancellationPolicy.description}
            </Text>
          </View>

          <View
            style={{
              paddingRight: 16,
              paddingLeft: 8,
              paddingTop: 8,
              gap: 8,
            }}
          >
            <Chip
              type={ChipType.DEFAULT}
              text={`Cancel before ${formatCancelUntil(
                car,
                cancellationPolicy.cancelUntil
              )}`}
            />
          </View>

          <View
            style={{
              paddingRight: 16,
              paddingLeft: 8,
              paddingTop: 8,
              gap: 8,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                gap: 8,
              }}
            >
              {cancellationPolicy.percentage > 0 ? (
                <>
                  <Text type="bodyBold">{`+$${formatNumber(
                    (car.dailyRate * cancellationPolicy.percentage) / 100
                  )}`}</Text>

                  <Text type="bodyBold">/ day</Text>
                </>
              ) : (
                <Text type="bodyBold">Free</Text>
              )}
            </View>
          </View>
        </View>
      </Card>
    </Pressable>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  container: {
    width: '100%',
    gap: 8,
  },
  card: {
    flexGrow: 1,
    flexDirection: {
      xs: 'row',
      md: 'column',
    },
    justifyContent: 'space-between',
    alignItems: {
      xs: 'center',
      md: 'flex-start',
    },
    padding: 16,
    gap: 16,
  },
  contentContainer: {
    gap: 8,
    width: {
      xs: '100%',
      md: '100%',
    },
  },
  default: {
    backgroundColor: theme.colors.card.default.backgroundColor,
    borderColor: theme.colors.card.default.borderColor,
  },
  active: {
    backgroundColor: theme.colors.card.active.backgroundColor,
    borderColor: theme.colors.card.active.borderColor,
    borderWidth: 1,
  },
  hover: {
    backgroundColor: theme.colors.card.hover.backgroundColor,
    borderColor: theme.colors.card.hover.borderColor,
  },
  disabled: {
    backgroundColor: theme.colors.card.disabled.backgroundColor,
    borderColor: theme.colors.card.disabled.borderColor,
  },
}));
